/**
 * Permission picker — tri-state grant tree built from the real catalog
 * (@momo/types PERMISSIONS/PERMISSION_META) grouped by domain.
 *
 * One component, two modes:
 *   EDIT (default): interactive — group glyphs toggle, leaf rows are
 *     schmancy-checkbox. Intents leave as `toggle-group` / `toggle-perm`
 *     CustomEvents; the consuming role editor binds @toggle-group /
 *     @toggle-perm and owns the mutation.
 *   READONLY (`readonly`): identical tree — same labels, same grouping,
 *     same tri-state group glyphs — but non-interactive. Granted leaves are
 *     marked and full-emphasis; ungranted recede. Used in member-detail,
 *     invite-detail, and role-permissions to show what a role grants —
 *     replacing the deleted permission-grants.view.ts, whose one job this
 *     mode now covers.
 *
 * Labels and descriptions come from PERMISSION_META (authoritative catalog).
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import {
	PERMISSION_META,
	PERMISSIONS,
	type Permission,
	PermissionDomain,
} from "@momo/types";
import { html, type TemplateResult } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";

const DOMAINS = PermissionDomain.options;

const DOMAIN_LABEL_KEY = {
	events: "admin.members.permissionPicker.domainEvents",
	orders: "admin.members.permissionPicker.domainOrders",
	guests: "admin.members.permissionPicker.domainGuests",
	checkin: "admin.members.permissionPicker.domainCheckin",
	reports: "admin.members.permissionPicker.domainReports",
	members: "admin.members.permissionPicker.domainMembers",
} as const;

const DOMAIN_ICON: Record<(typeof DOMAINS)[number], string> = {
	events: "event",
	orders: "receipt_long",
	guests: "redeem",
	checkin: "qr_code_scanner",
	reports: "bar_chart",
	members: "group",
};

const PERMISSIONS_BY_DOMAIN: Record<
	(typeof DOMAINS)[number],
	readonly Permission[]
> = {
	events: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "events"),
	orders: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "orders"),
	guests: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "guests"),
	checkin: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "checkin"),
	reports: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "reports"),
	members: PERMISSIONS.filter((p) => PERMISSION_META[p].domain === "members"),
};

@schmancyElement("permission-picker")
export class PermissionPicker extends LocalizeMixin(SchmancyElement) {
	/** The currently granted set. Property down — never mutated here. */
	@property({ attribute: false }) selected: Set<Permission> = new Set();
	/** Readonly view — render as marked/dimmed display, no click handlers. */
	@property({ type: Boolean }) readonly = false;

	override render(): TemplateResult {
		return html`
			<div class="flex flex-col gap-2">
				${repeat(
					DOMAINS,
					(d) => d,
					(domain) => {
						const perms = PERMISSIONS_BY_DOMAIN[domain];
						const sel = perms.filter((p) => this.selected.has(p)).length;
						const allOn = sel === perms.length;
						const someOn = sel > 0 && !allOn;
						const glyph = allOn
							? "check_box"
							: someOn
								? "indeterminate_check_box"
								: "check_box_outline_blank";
						const tint = this.classMap({
							"text-primary-default": allOn || someOn,
							"text-surface-onVariant": !(allOn || someOn),
						});
						return html`
							<schmancy-surface
								type="container"
								class="block rounded-xl p-3"
								role="group"
								aria-label=${this.t(
									"admin.members.permissionPicker.domainGroupAriaLabel",
									{
										domain: this.t(DOMAIN_LABEL_KEY[domain]),
									},
								)}
							>
								<div class="flex flex-col gap-2">
									<div class="flex items-center gap-2">
										${when(
											this.readonly,
											() => html`
												<span
													class="inline-grid place-items-center shrink-0 w-6 h-6"
													role="img"
													aria-label=${
														allOn
															? this.t(
																	"admin.members.permissionPicker.allGranted",
																)
															: someOn
																? this.t(
																		"admin.members.permissionPicker.someGranted",
																	)
																: this.t(
																		"admin.members.permissionPicker.noneGranted",
																	)
													}
												>
													<schmancy-icon class=${tint} aria-hidden="true">${glyph}</schmancy-icon>
												</span>
											`,
											() => html`
												<button
													class="inline-grid place-items-center bg-transparent border-0 p-0 cursor-pointer shrink-0 w-6 h-6 rounded outline-none hover:bg-primary-default/10 focus-visible:ring-2 focus-visible:ring-primary-default focus-visible:ring-offset-1 focus-visible:ring-offset-surface-container"
													role="checkbox"
													aria-label=${this.t("admin.members.permissionPicker.toggleDomain", { domain: this.t(DOMAIN_LABEL_KEY[domain]) })}
													aria-checked=${allOn ? "true" : someOn ? "mixed" : "false"}
													@click=${() =>
														this.dispatchEvent(
															new CustomEvent("toggle-group", {
																detail: { perms: [...perms], on: !allOn },
																bubbles: true,
																composed: true,
															}),
														)}
												>
													<schmancy-icon class=${tint} aria-hidden="true">${glyph}</schmancy-icon>
												</button>
											`,
										)}
										<schmancy-icon class="text-surface-onVariant" aria-hidden="true">${DOMAIN_ICON[domain]}</schmancy-icon>
										<schmancy-typography type="title" token="sm" class="flex-1">${this.t(DOMAIN_LABEL_KEY[domain])}</schmancy-typography>
										<schmancy-typography type="label" token="sm" class=${tint}> ${sel}/${perms.length} </schmancy-typography>
									</div>

									<div class="flex flex-col gap-2">
										${repeat(
											perms,
											(p) => p,
											(p) => {
												const granted = this.selected.has(p);
												const meta = PERMISSION_META[p];
												// Every control centres on the group toggle's 24px column. The
												// edit leaf's label is slotted into its checkbox so a click on
												// it toggles and it names the box; the checkbox draws an 18px box
												// plus an 8px gap, hence the 3px inset and the 29px description
												// indent that keep box and text on those same lines. The
												// checkbox only answers clicks on the label it draws, so the
												// element is kept that wide rather than stretched across the row.
												return when(
													this.readonly,
													() => html`
														<div class="flex items-start gap-2">
															<span class="inline-grid place-items-center shrink-0 w-6 h-6">
																<schmancy-icon
																	class=${this.classMap({
																		"text-primary-default": granted,
																		"text-surface-onVariant": !granted,
																	})}
																	aria-hidden="true"
																>
																	${granted ? "check_box" : "check_box_outline_blank"}
																</schmancy-icon>
															</span>
															<div class="flex flex-col min-w-0">
																<schmancy-typography type="body" token="sm" class=${this.classMap({ "min-h-6 flex items-center": true, "text-surface-onVariant": !granted })}>
																	${meta.label}
																</schmancy-typography>
																<schmancy-typography type="body" token="xs" class="text-surface-onVariant block">
																	${meta.description}
																</schmancy-typography>
															</div>
														</div>
													`,
													() => html`
														<div class="flex flex-col min-w-0">
															<schmancy-checkbox
																class="ml-0.75 self-start"
																.checked=${granted}
																@change=${(
																	e: CustomEvent<{ value: boolean }>,
																) =>
																	this.dispatchEvent(
																		new CustomEvent("toggle-perm", {
																			detail: { perm: p, on: e.detail.value },
																			bubbles: true,
																			composed: true,
																		}),
																	)}
															>
																<schmancy-typography type="body" token="sm" class="min-h-6 flex items-center">${meta.label}</schmancy-typography>
															</schmancy-checkbox>
															<schmancy-typography type="body" token="xs" class="text-surface-onVariant block pl-7.25">
																${meta.description}
															</schmancy-typography>
														</div>
													`,
												);
											},
										)}
									</div>
								</div>
							</schmancy-surface>
						`;
					},
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"permission-picker": PermissionPicker;
	}
}
